'use client';

import React, { useState, useEffect } from 'react';
import { Wallet, X, Loader2, ExternalLink } from 'lucide-react';
import { useWalletAuth } from '@/contexts/WalletAuthContext';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

interface WalletConnectButtonProps {
  label?: string;
  className?: string;
  disabled?: boolean;
}

interface WalletOption {
  id: string;
  name: string;
  description: string;
  emoji: string;
  color: string;
  bgColor: string;
  borderColor: string;
  action: 'injected' | 'mobile' | 'walletconnect' | 'coinbase' | 'trust' | 'phantom' | 'rainbow' | 'okx' | 'brave' | 'ledger';
  deepLink?: string;
  installUrl?: string;
}

const WALLET_OPTIONS: WalletOption[] = [
  {
    id: 'metamask',
    name: 'MetaMask',
    description: 'Browser extension wallet',
    emoji: '🦊',
    color: 'text-[#F6851B]',
    bgColor: 'bg-[rgba(246,133,27,0.12)]',
    borderColor: 'border-[rgba(246,133,27,0.25)]',
    action: 'injected',
    installUrl: 'https://metamask.io/download/',
  },
  {
    id: 'walletconnect',
    name: 'WalletConnect',
    description: 'Connect any mobile wallet via QR',
    emoji: '🔗',
    color: 'text-[#3B99FC]',
    bgColor: 'bg-[rgba(59,153,252,0.12)]',
    borderColor: 'border-[rgba(59,153,252,0.25)]',
    action: 'walletconnect',
    deepLink: 'https://walletconnect.com/',
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    description: 'Coinbase self-custody wallet',
    emoji: '🔵',
    color: 'text-[#0052FF]',
    bgColor: 'bg-[rgba(0,82,255,0.1)]',
    borderColor: 'border-[rgba(0,82,255,0.25)]',
    action: 'coinbase',
    deepLink: 'https://go.cb-w.com/dapp?cb_url=',
    installUrl: 'https://www.coinbase.com/wallet/downloads',
  },
  {
    id: 'trust',
    name: 'Trust Wallet',
    description: 'Multi-chain mobile wallet',
    emoji: '🛡️',
    color: 'text-[#3375BB]',
    bgColor: 'bg-[rgba(51,117,187,0.12)]',
    borderColor: 'border-[rgba(51,117,187,0.25)]',
    action: 'trust',
    deepLink: 'https://link.trustwallet.com/open_url?coin_id=60&url=',
    installUrl: 'https://trustwallet.com/download',
  },
  {
    id: 'phantom',
    name: 'Phantom',
    description: 'Solana & multi-chain wallet',
    emoji: '👻',
    color: 'text-[#AB9FF2]',
    bgColor: 'bg-[rgba(171,159,242,0.12)]',
    borderColor: 'border-[rgba(171,159,242,0.25)]',
    action: 'phantom',
    deepLink: 'https://phantom.app/ul/browse/',
    installUrl: 'https://phantom.app/download',
  },
  {
    id: 'rainbow',
    name: 'Rainbow',
    description: 'Fun & simple Ethereum wallet',
    emoji: '🌈',
    color: 'text-[#FF6B6B]',
    bgColor: 'bg-[rgba(255,107,107,0.1)]',
    borderColor: 'border-[rgba(255,107,107,0.25)]',
    action: 'rainbow',
    deepLink: 'https://rnbwapp.com/wc?uri=',
    installUrl: 'https://rainbow.me/download',
  },
  {
    id: 'okx',
    name: 'OKX Wallet',
    description: 'Web3 wallet by OKX exchange',
    emoji: '⬛',
    color: 'text-[#E6F1FF]',
    bgColor: 'bg-[rgba(255,255,255,0.06)]',
    borderColor: 'border-[rgba(255,255,255,0.15)]',
    action: 'okx',
    deepLink: 'okx://wallet/dapp/url?dappUrl=',
    installUrl: 'https://www.okx.com/web3',
  },
  {
    id: 'brave',
    name: 'Brave Wallet',
    description: 'Built into Brave browser',
    emoji: '🦁',
    color: 'text-[#FB542B]',
    bgColor: 'bg-[rgba(251,84,43,0.1)]',
    borderColor: 'border-[rgba(251,84,43,0.25)]',
    action: 'brave',
    installUrl: 'https://brave.com/download/',
  },
  {
    id: 'metamask_mobile',
    name: 'MetaMask Mobile',
    description: 'Open in MetaMask app',
    emoji: '📱',
    color: 'text-[#16C2FF]',
    bgColor: 'bg-[rgba(22,194,255,0.1)]',
    borderColor: 'border-[rgba(22,194,255,0.2)]',
    action: 'mobile',
  },
  {
    id: 'ledger',
    name: 'Ledger Live',
    description: 'Hardware wallet — most secure',
    emoji: '🔐',
    color: 'text-[#00E5A8]',
    bgColor: 'bg-[rgba(0,229,168,0.1)]',
    borderColor: 'border-[rgba(0,229,168,0.2)]',
    action: 'ledger',
    installUrl: 'https://www.ledger.com/ledger-live',
  },
];

export default function WalletConnectButton({ label = 'Connect Wallet', className = '', disabled = false }: WalletConnectButtonProps) {
  const { isConnected, walletAddress, walletUser, connectWallet, loading } = useWalletAuth();
  const [showModal, setShowModal] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [checkingWallet, setCheckingWallet] = useState(false);
  const [walletStatus, setWalletStatus] = useState<'idle' | 'checking' | 'registered' | 'new' | 'no_wallet'>('idle');
  const [connectedAddr, setConnectedAddr] = useState<string | undefined>(undefined);
  const [hasInjectedWallet, setHasInjectedWallet] = useState<boolean | null>(null);
  const [connectingWalletId, setConnectingWalletId] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHasInjectedWallet(!!(window as any).ethereum);
    }
  }, []);

  useEffect(() => {
    if (isConnected && walletAddress && walletAddress !== connectedAddr) {
      setConnectedAddr(walletAddress);
      checkWalletRegistration(walletAddress);
    }
  }, [isConnected, walletAddress]);

  useEffect(() => {
    if (walletUser && showModal) {
      setShowModal(false);
      setConnecting(false);
      setWalletStatus('idle');
      setConnectingWalletId(null);
      router.push('/dashboard');
    }
  }, [walletUser]);

  const checkWalletRegistration = async (addr: string) => {
    setCheckingWallet(true);
    setWalletStatus('checking');
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('onboarding_records')
        .select('completed')
        .eq('wallet_address', addr.toLowerCase())
        .single();

      if (data?.completed) {
        setWalletStatus('registered');
        setTimeout(() => {
          setShowModal(false);
          setConnecting(false);
          setWalletStatus('idle');
          setConnectingWalletId(null);
        }, 1200);
      } else {
        setWalletStatus('new');
        setTimeout(() => {
          setShowModal(false);
          setConnecting(false);
          setWalletStatus('idle');
          setConnectingWalletId(null);
        }, 1200);
      }
    } catch {
      setWalletStatus('new');
      setTimeout(() => {
        setShowModal(false);
        setConnecting(false);
        setWalletStatus('idle');
        setConnectingWalletId(null);
      }, 1200);
    } finally {
      setCheckingWallet(false);
    }
  };

  const handleWalletOption = (option: WalletOption) => {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : 'https://aidaram.net';
    const cleanUrl = currentUrl.replace(/^https?:\/\//, '');

    // Dev/QA only: when the mock wallet is enabled, connect it directly regardless of which
    // option is clicked (no browser extension is present in automated test environments).
    if (process.env.NEXT_PUBLIC_ENABLE_MOCK_WALLET === 'true') {
      setConnectingWalletId(option.id);
      setConnecting(true);
      connectWallet();
      return;
    }

    switch (option.action) {
      case 'injected': case'brave':
        if (!(window as any).ethereum) {
          if (option.installUrl) window.open(option.installUrl, '_blank');
          return;
        }
        setConnectingWalletId(option.id);
        setConnecting(true);
        connectWallet();
        setTimeout(() => {
          if (!isConnected) { setConnecting(false); setConnectingWalletId(null); }
        }, 5000);
        break;

      case 'mobile':
        window.open(`https://metamask.app.link/dapp/${cleanUrl}`, '_blank');
        break;

      case 'walletconnect':
        // Try injected first, fall back to WalletConnect info
        if ((window as any).ethereum) {
          setConnectingWalletId(option.id);
          setConnecting(true);
          connectWallet();
          setTimeout(() => {
            if (!isConnected) { setConnecting(false); setConnectingWalletId(null); }
          }, 5000);
        } else {
          window.open('https://walletconnect.com/', '_blank');
        }
        break;

      case 'coinbase':
        if ((window as any).ethereum?.isCoinbaseWallet) {
          setConnectingWalletId(option.id);
          setConnecting(true);
          connectWallet();
          setTimeout(() => {
            if (!isConnected) { setConnecting(false); setConnectingWalletId(null); }
          }, 5000);
        } else {
          window.open(`https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(currentUrl)}`, '_blank');
        }
        break;

      case 'trust':
        window.open(`https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(currentUrl)}`, '_blank');
        break;

      case 'phantom':
        if ((window as any).solana?.isPhantom || (window as any).phantom) {
          setConnectingWalletId(option.id);
          setConnecting(true);
          connectWallet();
          setTimeout(() => {
            if (!isConnected) { setConnecting(false); setConnectingWalletId(null); }
          }, 5000);
        } else {
          window.open(`https://phantom.app/ul/browse/${encodeURIComponent(currentUrl)}?ref=${encodeURIComponent(currentUrl)}`, '_blank');
        }
        break;

      case 'rainbow':
        window.open(`https://rnbwapp.com/wc?uri=${encodeURIComponent(currentUrl)}`, '_blank');
        break;

      case 'okx':
        if ((window as any).okxwallet) {
          setConnectingWalletId(option.id);
          setConnecting(true);
          connectWallet();
          setTimeout(() => {
            if (!isConnected) { setConnecting(false); setConnectingWalletId(null); }
          }, 5000);
        } else {
          window.open(`okx://wallet/dapp/url?dappUrl=${encodeURIComponent(currentUrl)}`, '_blank');
        }
        break;

      case 'ledger':
        window.open('https://www.ledger.com/ledger-live', '_blank');
        break;
    }
  };

  if (isConnected && walletAddress) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(22,194,255,0.08)] border border-[rgba(22,194,255,0.15)] rounded-lg">
          <Wallet size={12} className="text-[#16C2FF]" />
          <span className="text-xs font-mono text-[#16C2FF]">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => { if (!disabled) { setWalletStatus('idle'); setShowModal(true); } }}
        disabled={disabled}
        className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#16C2FF] to-[#0EA5E9] text-[#07111F] font-semibold rounded-xl text-sm hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      >
        <Wallet size={14} />
        {label}
      </button>

      {showModal && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4">
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.2)] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[rgba(22,194,255,0.1)] sticky top-0 bg-[#0D1B2A] z-10">
              <div>
                <h3 className="text-base font-sora font-semibold text-[#E6F1FF]">Connect Wallet</h3>
                <p className="text-[11px] text-[#4A5568] mt-0.5">Choose your preferred wallet to connect</p>
              </div>
              <button onClick={() => { setShowModal(false); setConnecting(false); setWalletStatus('idle'); setConnectingWalletId(null); }} className="text-[#93A4B8] hover:text-[#E6F1FF] p-1">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              {/* Status screens */}
              {walletStatus === 'checking' && (
                <div className="flex flex-col items-center py-6 gap-3">
                  <Loader2 size={28} className="animate-spin text-[#16C2FF]" />
                  <p className="text-sm text-[#93A4B8]">Checking wallet...</p>
                </div>
              )}
              {walletStatus === 'registered' && (
                <div className="flex flex-col items-center py-6 gap-3">
                  <div className="w-12 h-12 rounded-full bg-[rgba(0,229,168,0.15)] border border-[rgba(0,229,168,0.3)] flex items-center justify-center">
                    <Wallet size={20} className="text-[#00E5A8]" />
                  </div>
                  <p className="text-sm text-[#00E5A8] font-medium">Welcome back! Redirecting to dashboard...</p>
                </div>
              )}
              {walletStatus === 'new' && (
                <div className="flex flex-col items-center py-6 gap-3">
                  <div className="w-12 h-12 rounded-full bg-[rgba(22,194,255,0.15)] border border-[rgba(22,194,255,0.3)] flex items-center justify-center">
                    <Wallet size={20} className="text-[#16C2FF]" />
                  </div>
                  <p className="text-sm text-[#16C2FF] font-medium">New wallet detected — completing setup...</p>
                </div>
              )}

              {/* Wallet options grid */}
              {(walletStatus === 'idle') && (
                <>
                  <p className="text-xs text-[#93A4B8] text-center pb-1">
                    Connect your wallet to sign in. New users will be guided through email verification.
                  </p>

                  <div className="grid grid-cols-1 gap-2">
                    {WALLET_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleWalletOption(option)}
                        disabled={connecting}
                        className={`w-full flex items-center gap-3 px-4 py-3 bg-[rgba(255,255,255,0.02)] border ${option.borderColor} rounded-xl hover:bg-[rgba(255,255,255,0.05)] transition-all disabled:opacity-60 group`}
                      >
                        <div className={`w-9 h-9 rounded-lg ${option.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-lg leading-none">{option.emoji}</span>
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${option.color}`}>{option.name}</p>
                          <p className="text-xs text-[#4A5568]">{option.description}</p>
                        </div>
                        {connectingWalletId === option.id ? (
                          <Loader2 size={14} className="animate-spin text-[#16C2FF] flex-shrink-0" />
                        ) : (
                          <ExternalLink size={13} className="text-[#4A5568] group-hover:text-[#93A4B8] transition-colors flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>

                  <p className="text-[10px] text-[#4A5568]/70 text-center pt-1">
                    By connecting, you agree to our Terms of Service
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
